import React, { useEffect, useState, useCallback } from 'react';
import { SignIn, useClerk, useSignIn } from '@clerk/clerk-react';
import { useConfig } from '../contexts/ConfigContext';

// Simple Clerk auth page with separate Email/Password and Google OAuth
const SimpleSignInPage = () => {
  const [isProcessingHandshake, setIsProcessingHandshake] = useState(false);
  const [handshakeStatus, setHandshakeStatus] = useState('');
  const clerk = useClerk();
  const { signIn, setActive, isLoaded } = useSignIn();
  const { isEmailPasswordAuthEnabled, isLoading: isConfigLoading } = useConfig();

  // Config logging for debugging
  console.log('[SimpleSignInPage] Config state:', { isEmailPasswordAuthEnabled, isConfigLoading });

  // Track Clerk state changes for debugging
  useEffect(() => {
    if (clerk) {
      console.log('[SimpleSignInPage] 📊 Clerk state:', {
        loaded: clerk.loaded,
        user: clerk.user?.id || 'none',
        session: clerk.session?.id || 'none'
      });
    }
  }, [clerk, clerk?.loaded, clerk?.user, clerk?.session]);

  // Handler for sign-in completion
  const handleSignInComplete = useCallback(async () => {
    try {
      if (signIn?.status === 'complete' && signIn.createdSessionId) {
        console.log('[SimpleSignInPage] Activating session after sign-in completion');

        // Use modern setActive instead of deprecated setSession
        if (typeof setActive === 'function') {
          await setActive({ session: signIn.createdSessionId });
          console.log('[SimpleSignInPage] ✅ Session activated successfully');
        } else {
          console.error('[SimpleSignInPage] ❌ setActive function not available');
        }
      }
    } catch (error) {
      console.error('[SimpleSignInPage] ❌ Error activating session:', error);
    }
  }, [signIn, setActive]);

  // Track signIn state changes for debugging
  useEffect(() => {
    if (signIn && isLoaded) {
      console.log('[SimpleSignInPage] SignIn state updated:', {
        status: signIn.status,
        firstFactorVerification: signIn.firstFactorVerification?.status,
        identifier: signIn.identifier || 'none',
        isLoaded: isLoaded
      });

      // Track status for critical logic
      if (signIn.status === 'needs_identifier') {
        console.log('[SimpleSignInPage] Waiting for email input');
      } else if (signIn.status === 'needs_first_factor') {
        console.log('[SimpleSignInPage] Waiting for password input');
      } else if (signIn.status === 'complete') {
        console.log('[SimpleSignInPage] Sign in completed!');

        // Critical: Activate session on sign-in completion
        handleSignInComplete();
      }
    }
  }, [signIn, signIn?.status, signIn?.firstFactorVerification?.status, signIn?.identifier, isLoaded, handleSignInComplete]);

  // Moderator appearance: hides social buttons, shows only email/password
  const moderatorAppearance = {
    baseTheme: 'dark',
    elements: {
      socialButtons: { display: 'none' }, // Hide Apple/Facebook/Google buttons
      dividerRow: { display: 'none' }, // Hide "OR" divider
      formButtonPrimary: 'bg-orange-500 hover:bg-orange-600',
      footerActionLink: 'text-orange-500 hover:text-orange-600'
    }
  };

  // Custom Google OAuth with explicit redirect to oauth-callback.html
  const handleGoogleOAuth = async () => {
    if (!isLoaded || !signIn) {
      console.log('[SimpleSignInPage] Clerk not loaded yet');
      return;
    }

    try {
      console.log('[SimpleSignInPage] Starting Google OAuth with redirectUrl only (no redirectUrlComplete conflict)');

      // Only redirectUrl for clean redirect page (removed conflicting redirectUrlComplete)
      await signIn.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: `${window.location.origin}/calculator/oauth-success.html`,  // Dynamic redirect URL
      });

      console.log('[SimpleSignInPage] Google OAuth initiated - redirecting to oauth-success.html');
    } catch (error) {
      console.error('[SimpleSignInPage] Google OAuth error:', error);
    }
  };

  useEffect(() => {
    // Function to extract token from JWT handshake (copied from oauth-callback.html)
    const extractTokenFromHandshake = (handshakeParam) => {
      try {
        const base64Url = handshakeParam.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );

        const payload = JSON.parse(jsonPayload);

        if (payload.handshake && Array.isArray(payload.handshake)) {
          const sessionCookie = payload.handshake.find(cookie =>
            typeof cookie === 'string' && cookie.includes('__session=')
          );

          if (sessionCookie) {
            const tokenMatch = sessionCookie.match(/__session=([^;]+)/);
            if (tokenMatch && tokenMatch[1]) {
              return tokenMatch[1];
            }
          }
        }

        return null;
      } catch (e) {
        console.error('[SimpleSignInPage] Error extracting token from handshake:', e);
        return null;
      }
    };

    const processHandshakeOnSignInPage = async (handshakeParam) => {
      try {
        console.log('[SimpleSignInPage] Starting handshake processing on sign-in page');

        // Extract token directly from JWT handshake (method from oauth-callback.html)
        const directToken = extractTokenFromHandshake(handshakeParam);

        if (directToken) {
          console.log('[SimpleSignInPage] Token successfully extracted from handshake');
          setHandshakeStatus('Token received, authenticating...');

          // Save token to localStorage
          localStorage.setItem('__session', directToken);
          localStorage.setItem('clerk_auth_complete', 'true');

          // Dispatch event for AuthContext
          window.dispatchEvent(new CustomEvent('clerkSessionTokenReady', {
            detail: {
              token: directToken,
              completed: true,
              source: 'sign-in-page-handshake'
            }
          }));

          setHandshakeStatus('Successfully authenticated!');

          // Clear URL from handshake parameter
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);

          // Hide message after 1 second - user should be authenticated
          setTimeout(() => {
            setIsProcessingHandshake(false);
          }, 1000);

        } else {
          console.log('[SimpleSignInPage] Could not extract token from handshake, using Clerk API');
          setHandshakeStatus('Completing authentication via Clerk...');

          // Fallback: use standard Clerk handleRedirectCallback
          if (clerk && clerk.loaded) {
            await clerk.handleRedirectCallback();
            setHandshakeStatus('Authentication completed');

            // Clear URL
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);

            setTimeout(() => setIsProcessingHandshake(false), 1000);
          } else {
            throw new Error('Clerk not loaded');
          }
        }

      } catch (error) {
        console.error('[SimpleSignInPage] Error processing handshake:', error);
        setHandshakeStatus('Authentication error');

        // Clear URL even on error
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);

        setTimeout(() => setIsProcessingHandshake(false), 3000);
      }
    };

    // Check for _clerk_handshake parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const handshakeParam = urlParams.get('_clerk_handshake');

    if (handshakeParam) {
      console.log('[SimpleSignInPage] Detected _clerk_handshake parameter, processing OAuth callback');
      setIsProcessingHandshake(true);
      setHandshakeStatus('Processing OAuth...');

      processHandshakeOnSignInPage(handshakeParam);
    }
  }, [clerk]);

  // If processing handshake, show success overlay
  if (isProcessingHandshake) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4" style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
      }}>
        <div className="w-full max-w-lg text-center">
          <div className="bg-slate-800/90 backdrop-blur-xl p-8 rounded-2xl border border-slate-600/50 shadow-2xl">
            {/* Success Icon */}
            <div className="text-6xl mb-6 animate-bounce">🎉</div>
            
            {/* Title */}
            <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
              Authentication Successful!
            </h2>
            
            {/* Subtitle */}
            <p className="text-xl text-slate-200 mb-6 font-medium">
              Welcome to <span className="text-orange-500 font-semibold">QUER</span>
            </p>
            
            {/* Progress Steps */}
            <div className="bg-slate-900/60 p-6 rounded-xl mb-6 border border-orange-500/20">
              <div className="space-y-3">
                <div className="flex items-center justify-center text-green-400">
                  <span className="mr-3 text-lg">✅</span>
                  <span>OAuth authentication completed</span>
                </div>
                <div className="flex items-center justify-center text-green-400">
                  <span className="mr-3 text-lg">✅</span>
                  <span>Secure connection established</span>
                </div>
                <div className="flex items-center justify-center text-orange-400">
                  <span className="mr-3 text-lg">🔄</span>
                  <span>Switching to desktop app</span>
                  <div className="ml-3 animate-spin rounded-full h-5 w-5 border-2 border-orange-400 border-t-transparent"></div>
                </div>
              </div>
            </div>
            
            {/* Status Message */}
            <p className="text-slate-400 text-sm leading-relaxed">
              {handshakeStatus || 'Processing OAuth completion...'}<br/>
              <span className="text-orange-500 font-medium">QUER Calculator</span> will open automatically.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Config loading check - moved to end after all hooks!
  if (isConfigLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
          <p className="text-slate-400">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-md space-y-4">
        {/* Conditional rendering logic */}
        {isEmailPasswordAuthEnabled ? (
          <>
            {/* === MODERATOR MODE === */}
            <SignIn
              signUpUrl="/sign-up"
              appearance={moderatorAppearance}
            />
          </>
        ) : (
          <>
            {/* === REGULAR USER MODE === */}
            <div className="text-center">
              <h1 className="text-3xl font-bold text-slate-200 mb-2">QUER</h1>
              <p className="text-slate-400 mb-8">Sign in to continue to the terminal</p>
              <button
                onClick={handleGoogleOAuth}
                disabled={!isLoaded}
                // Minimalist style
                className="w-full flex items-center justify-center px-4 py-2 border border-slate-600 rounded-md shadow-sm bg-transparent text-slate-300 hover:bg-slate-800 hover:border-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign In with Google
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SimpleSignInPage;